import { useEffect, useRef, useState } from "react";
import { getNegotiationStatus } from "../../../../services/negotiation/negotiationService";

const FAST_INTERVAL_MS = 4000;   // while at least one transporter is being processed by the agent
const IDLE_INTERVAL_MS = 20000;  // otherwise -- just enough to notice a new round starting

/**
 * Polls GET /negotiation/{truck_enquiry_id}/status and derives a per-transporter processing
 * indicator for the UI. Adaptive cadence: fast while any transporter is `processing`, slow
 * otherwise -- the slow tick doubles as `onIdleTick`, so the caller can still refresh the
 * heavier agent-settings/rates endpoints occasionally (to notice changes the agent had no part
 * in, e.g. a transporter submitting a fresh quote) without polling those endpoints on a fixed
 * short timer regardless of whether anything is actually happening.
 *
 * On a processing -> terminal transition, `onTransition` fires once immediately, ignoring the
 * tick cadence, so the resolved outcome (countered / declined / failed) shows up right away
 * instead of waiting for the next scheduled poll.
 *
 * Backs off to the slow cadence on a failed poll rather than retrying a failing endpoint quickly.
 */
export default function useNegotiationStatusPolling({
  truckEnquiryId,
  enabled,
  onIdleTick,
  onTransition,
}) {
  const [transporterStatuses, setTransporterStatuses] = useState({});
  const lastSeenRef = useRef({});
  const isFirstPollRef = useRef(true);
  const timeoutRef = useRef(null);
  const callbacksRef = useRef({ onIdleTick, onTransition });
  callbacksRef.current = { onIdleTick, onTransition };

  useEffect(() => {
    if (!enabled || !truckEnquiryId) {
      setTransporterStatuses({});
      lastSeenRef.current = {};
      isFirstPollRef.current = true;
      return;
    }

    let cancelled = false;

    const scheduleNext = (delay) => {
      if (cancelled) return;
      timeoutRef.current = setTimeout(poll, delay);
    };

    const poll = async () => {
      let transporters;
      try {
        const data = await getNegotiationStatus(truckEnquiryId);
        transporters = data?.transporters || [];
      } catch (err) {
        console.error(err);
        scheduleNext(IDLE_INTERVAL_MS);
        return;
      }
      if (cancelled) return;

      const nextMap = {};
      let anyProcessing = false;
      let transitioned = false;

      transporters.forEach((t) => {
        nextMap[t.transporter_id] = t;
        if (t.status === "processing") anyProcessing = true;
        const prev = lastSeenRef.current[t.transporter_id];
        if (prev?.status === "processing" && t.status !== "processing") {
          transitioned = true;
        }
      });

      lastSeenRef.current = Object.fromEntries(
        transporters.map((t) => [t.transporter_id, { status: t.status, current_round: t.current_round }])
      );
      setTransporterStatuses(nextMap);

      const wasFirstPoll = isFirstPollRef.current;
      isFirstPollRef.current = false;

      if (transitioned) {
        callbacksRef.current.onTransition?.();
      } else if (!anyProcessing && !wasFirstPoll) {
        // The caller already did an initial load on mount -- only fire the idle heartbeat
        // on subsequent ticks, not redundantly right after that initial load.
        callbacksRef.current.onIdleTick?.();
      }

      scheduleNext(anyProcessing ? FAST_INTERVAL_MS : IDLE_INTERVAL_MS);
    };

    poll();

    return () => {
      cancelled = true;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [truckEnquiryId, enabled]);

  return { transporterStatuses };
}
